import React, { useMemo } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { EmployeLeaveTypeSummary } from "../../models/leave-type.dto";

ChartJS.register(ArcElement, Tooltip, Legend);
ChartJS.defaults.plugins.legend.display = false;

const LeaveDaysChart = ({
	numberOfDaysLeft,
	numberOfDaysPending,
	numberOfDaysUsed,
	numberOfCarryOverDays,
	numberOfCarryOverDaysUsed,
}: EmployeLeaveTypeSummary) => {
	const hasCarryOver =
		numberOfCarryOverDays !== undefined && numberOfCarryOverDays > 0;
	const carryOverDaysLeft = hasCarryOver
		? (numberOfCarryOverDays ?? 0) - (numberOfCarryOverDaysUsed ?? 0)
		: 0;

	const data = useMemo(() => {
		if (numberOfDaysLeft || hasCarryOver) {
			const segments = [
				numberOfDaysLeft ?? 0,
				numberOfDaysPending ?? 0,
				numberOfDaysUsed ?? 0,
			];
			if (hasCarryOver) {
				segments.push(carryOverDaysLeft);
				segments.push(numberOfCarryOverDaysUsed ?? 0);
			}
			return segments;
		}
		return [100, 0];
	}, [
		numberOfDaysLeft,
		numberOfDaysPending,
		numberOfDaysUsed,
		hasCarryOver,
		carryOverDaysLeft,
		numberOfCarryOverDaysUsed,
	]);

	const backgroundColor = useMemo(() => {
		if (numberOfDaysLeft || hasCarryOver) {
			const colors = ["#27AE60", "#6978F0", "#F2F2F4"];
			if (hasCarryOver) {
				colors.push("#F2994A"); // carry-over days left
				colors.push("#D4D4D8"); // carry-over days used
			}
			return colors;
		}
		return ["#F2F2F4", "#F2F2F4"];
	}, [numberOfDaysLeft, hasCarryOver]);

	const chartData = useMemo(() => {
		const labels = [
			`${numberOfDaysLeft ?? 0} days leave days`,
			`${numberOfDaysPending ?? 0} days pending`,
			`${numberOfDaysUsed ?? 0} days used`,
		];
		if (hasCarryOver) {
			labels.push(`${carryOverDaysLeft} carry-over days left`);
			labels.push(`${numberOfCarryOverDaysUsed ?? 0} carry-over days used`);
		}

		return {
			labels,
			datasets: [
				{
					label: "# of days",
					data,
					backgroundColor,
					borderWidth: 1,
					hoverOffset: 4,
				},
			],
		};
	}, [
		data,
		backgroundColor,
		hasCarryOver,
		carryOverDaysLeft,
		numberOfCarryOverDaysUsed,
	]);

	return <Doughnut options={{ cutout: 100 }} data={chartData} />;
};

export default LeaveDaysChart;
